import { Box, Button, Divider, Heading, HStack, Text, useToast, VStack } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";

import { $ok } from "foundation-ts/commons";
import { Nullable } from "foundation-ts/types";

import { getPaymentStatus, initiatePayment, Order, OrderLine } from "@services/payment.service";
import { defaultErrorToast } from "@utils/toast";

export const PaymentTestView = () => {
    const [products, setProducts] = useState<Product[]>([
        { id: '1', name: 'Produit A', price: 29.99, quantity: 2 },
        { id: '2', name: 'Produit B', price: 49.99, quantity: 1 }
    ]);
    const [step, setStep] = useState<'products' | 'payment-methods' | 'processing' | 'result'>('products');
    const [result, setResult] = useState<Nullable<string>>(null);
    const [sessionURL, setSessionURL] = useState<Nullable<string>>(null);
    const [sessionID, setSessionID] = useState<Nullable<string>>(null);
    const toast = useToast();
    const oldHeight = useRef<number>(0);
    const total = products.reduce((sum, p) => sum + p.price * p.quantity, 0);
    const tax = products.reduce((sum, p) => sum + p.price * p.quantity * 0.2, 0);

    const handleOrder = async () => {
        const orderLines: OrderLine[] = products.map(product => ({
            ITN: product.id,
            DSC: product.name,
            QTY: product.quantity,
            UPR: product.price,
            NET: product.price * product.quantity,
            GRS: product.price * product.quantity * 1.2,
            TAX: product.price * product.quantity * 0.2,
        }));

        const order: Order = {
            orderId: `ORDER-${Date.now()}`,
            amount: {
                value: total,
                currency: 'EUR'
            },
            orderLines,
            customer: {
                email: 'customer@example.com',
                firstName: 'John',
                lastName: 'Doe',
                billingAddress: {
                    street: '123 Rue Example',
                    city: 'Paris',
                    country: 'FR',
                    zipCode: '75001'
                }
            }
        };

        setStep('processing');
        const createdPayment = await initiatePayment(orderLines);
        if ($ok(createdPayment)) {
            setSessionURL(createdPayment?.sessionURL);
            setSessionID(createdPayment?.sessionID);
            setStep("payment-methods");
        } else {
            toast(defaultErrorToast("Erreur lors de la création de la session de paiement"));
        }
    }

    const handleFrameMessage = async (event: MessageEvent) => {
        console.log("handleFrameMessage");
        console.log(event);
        if (!$ok(event.data)) { return; }

        const data = event.data as string;
        if (data.includes("new_height: ")) {
            event.preventDefault();
            const h = Number(data.substring(11)) + 25;
            if (h > 100 && h !== oldHeight.current) {
                const iframe = document.getElementById('payment-frame');
                if (!$ok(iframe)) { return; }
                iframe!.style.height = `${oldHeight.current = h}px`;
            }
        } else {
            event.preventDefault();
            const paymentStatus = await getPaymentStatus(sessionID!);
            console.log(paymentStatus);
            setStep('result');
            if (event.data == 'payment_success') {
                setResult(`Merci, le paiement a été validé par ${paymentStatus?.paymentMethod}`);
            } else if (data == 'payment_cancel') {
                setResult('Le paiement a été annulé');
            } else if (data == 'payment_failure') {
                setResult(`Désolé mais le paiement a échoué (${paymentStatus?.returnCode})`);
            } else if (data == 'payment_review') {
                setResult("Je ne sais pas ce qu'est le paymentReview");
            }
        }
    }

    const renderProductsStep = () => (
        <VStack spacing={6} align="stretch">
            <Heading size="lg">Votre panier</Heading>
      
            {products.map(product => (
                <Box key={product.id} p={4} borderWidth={1} borderRadius="md">
                    <HStack justify="space-between">
                        <VStack align="start" spacing={1}>
                            <Text fontWeight="bold">{product.name}</Text>
                            <Text fontSize="sm" color="gray.600">Quantité: {product.quantity}</Text>
                        </VStack>
                        <Text fontWeight="bold">{(product.price * product.quantity).toFixed(2)} € HT</Text>
                    </HStack>
                </Box>
            ))}

            <Divider />

            <HStack justify="space-between">
                <Text fontSize="l" fontWeight="bold">Total</Text>
                <Text fontSize="l" fontWeight="bold">{total.toFixed(2)} € HT</Text>
            </HStack>
            <HStack justify="space-between">
                <Text fontSize="sm" fontWeight="bold">Tax</Text>
                <Text fontSize="sm" fontWeight="bold">{tax.toFixed(2)} €</Text>
            </HStack>
            <HStack justify="space-between">
                <Text fontSize="xl" fontWeight="bold">Total TTC</Text>
                <Text fontSize="xl" fontWeight="bold">{(total + tax).toFixed(2)} €</Text>
            </HStack>

            <Button colorScheme="blue" size="lg" onClick={handleOrder}>Commander</Button>
        </VStack>
    )

    const renderPaymentMethodStep = () => (
        <VStack spacing={6} align="stretch">
            <Heading size="lg">Choix du mode de paiement</Heading>
            <iframe id="payment-frame" src={sessionURL!} />
        </VStack>
    )

    const renderResultStep = () => (
        <VStack spacing={6} align="stretch">
            <Heading size="lg">Résultat du paiement</Heading>
            <Box>{result}</Box>
        </VStack>
    )

    useEffect(() => {
        console.log("useEffect PaymentTestView");
        window.addEventListener("message", handleFrameMessage);
        return () => { 
            window.removeEventListener("message", handleFrameMessage);
        };
    }, []);

    return (
        <Box maxW="800px" width="500px" mx="auto" p={8}>
            {step === 'products' && renderProductsStep()}
            {step === 'payment-methods' && renderPaymentMethodStep()}
            {step === 'result' && renderResultStep()}
        </Box>
    );
};

interface Product {
    id: string;
    name: string;
    price: number;
    quantity: number;
}